import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  AdminApiPaths,
  TApiResponse,
  APIDataProvider,
  TApiPaginationResponse,
} from '../../../api';
import {
  ICoursePricingTemplate,
  ICoursePricingTemplateDeleteVerificationData,
  TCoursePricingTemplateSubscriptionPricingBillingPeriodTypes,
} from '.';

export type TAdminCreateCoursePricingTemplate = {
  name: string;
  credit: boolean;
  oneTime: boolean;
  subscription: boolean;
  oneTimePricings?: Array<{
    currency: string;
    amount: number;
    country?: string;
  }>;
  subscriptionPricings?: Array<{
    currency: string;
    amount: number;
    billingPeriod: {
      count: number;
      type: TCoursePricingTemplateSubscriptionPricingBillingPeriodTypes;
    };
    country?: string;
  }>;
};

export type TAdminUpdateCoursePricingTemplate = {
  id: string;
  name: string;
  credit: boolean;
  oneTime: boolean;
  subscription: boolean;
  oneTimePricings?: Array<{
    currency: string;
    amount: number;
    country?: string;
  }>;
  subscriptionPricings?: Array<{
    currency: string;
    amount: number;
    billingPeriod: {
      count: number;
      type: TCoursePricingTemplateSubscriptionPricingBillingPeriodTypes;
    };
    country?: string;
  }>;
};

export type TAdminGetCoursePricingTemplates = {
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  searchText?: string;
};

const apiDataProvider = new APIDataProvider();

const adminGetCoursePricingTemplatesModule = async ({
  limit,
  offset,
  sortKey,
  sortValue,
  searchText,
}: TAdminGetCoursePricingTemplates) => {
  let path = `${AdminApiPaths.adminCoursePricingTemplates}?limit=${limit}&offset=${offset}`;

  if (sortKey) {
    path += `&sortKey=${sortKey}`;
  }

  if (sortKey) {
    path += `&sortValue=${sortValue}`;
  }

  if (searchText) {
    path += `&searchText=${searchText}`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<TApiPaginationResponse<ICoursePricingTemplate>>
  >(path);

  return response.data;
};

export const adminCreateCoursePricingTemplate = createAsyncThunk(
  '/admin/course-pricing-templates/create',
  async (data: TAdminCreateCoursePricingTemplate) => {
    const path = `${AdminApiPaths.adminCoursePricingTemplates}`;

    const response = await apiDataProvider.post<
      TApiResponse<ICoursePricingTemplate>
    >(path, data);

    return response.data;
  }
);

export const adminUpdateCoursePricingTemplate = createAsyncThunk(
  '/admin/course-pricing-templates/update',
  async (data: TAdminUpdateCoursePricingTemplate) => {
    const path = `${AdminApiPaths.adminCoursePricingTemplates}/${data.id}`;

    const response = await apiDataProvider.put<
      TApiResponse<ICoursePricingTemplate>
    >(path, data);

    return response.data;
  }
);

export const adminDeleteCoursePricingTemplate = createAsyncThunk(
  '/admin/course-pricing-templates/delete',
  async (id: string) => {
    const path = `${AdminApiPaths.adminCoursePricingTemplates}/${id}`;

    const response = await apiDataProvider.delete<
      TApiResponse<ICoursePricingTemplate>
    >(path);

    return response.data;
  }
);

export const adminVerifyCoursePricingTemplateDelete = createAsyncThunk(
  '/admin/course-pricing-templates/verify-delete',
  async (id: string) => {
    const path = `${AdminApiPaths.adminCoursePricingTemplates}/verify-delete/${id}`;

    const response = await apiDataProvider.get<
      TApiResponse<ICoursePricingTemplateDeleteVerificationData>
    >(path);

    return response.data;
  }
);

export const adminGetCoursePricingTemplates = createAsyncThunk(
  '/admin/course-pricing-templates/list',
  adminGetCoursePricingTemplatesModule
);

export const adminGetCoursePricingTemplatesForDropdown = createAsyncThunk(
  '/admin/course-pricing-templates/dropdown-list',
  adminGetCoursePricingTemplatesModule
);
